// src/shared/components/ui/MinimalisticDropDown.tsx
import React from 'react'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem                     from '@mui/material/MenuItem'
import { SxProps, Theme }           from '@mui/material/styles'

/* ── public types ─────────────────────────────────────────── */

export interface Option<T extends string | number = string | number> {
  value: T
  label: React.ReactNode
}

export interface MinimalisticDropDownProps<
  T extends string | number = string | number,
> {
  value: T | ''
  options: Option<T>[]
  onChange: (v: T | '') => void

  /* optional */
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
  minWidth?: number | string
  sx?: SxProps<Theme>
  menuSx?: SxProps<Theme>
}

/* ── dark-theme tokens ───────────────────────────────────── */

const baseSelectSx = {
  bgcolor: 'rgb(38 38 38)',          // neutral-800
  color  : '#f3f4f6',                // neutral-100
  '& .MuiSvgIcon-root': { color: '#a1a1aa' },
  '& fieldset'            : { borderColor: '#52525b' },
  '&:hover fieldset'      : { borderColor: '#737373' },
  '&.Mui-focused fieldset': { borderColor: 'rgb(99 102 241)' }, // indigo-500
} as const

const baseMenuSx = {
  bgcolor: 'rgb(38 38 38)',
  color  : '#f3f4f6',
  '& .MuiMenuItem-root:hover': { bgcolor: '#52525b' },
  '& .Mui-selected': {
    bgcolor: 'rgb(99 102 241) !important',
    color  : '#f3f4f6',
  },
} as const

/* ── component ───────────────────────────────────────────── */

function MinimalisticDropDown<
  T extends string | number = string | number,
>({
  value,
  options,
  onChange,
  placeholder = '— none —',
  disabled,
  size = 'small',
  minWidth = 180,
  sx,
  menuSx,
}: MinimalisticDropDownProps<T>) {
  const handleChange = (e: SelectChangeEvent<unknown>) =>
    onChange(e.target.value as T | '')

  /* merge SX into flat objects (avoids nested-array TS complaints) */
  const rootSx: SxProps<Theme> = {
    minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth,
    ...baseSelectSx,
    ...(sx as any),
  }

  const popupSx: SxProps<Theme> = {
    ...baseMenuSx,
    ...(menuSx as any),
  }

  return (
    <Select
      size={size}
      value={value as any}
      displayEmpty
      disabled={disabled}
      onChange={handleChange}
      sx={rootSx}
      MenuProps={{ PaperProps: { sx: popupSx } }}
    >
      <MenuItem value="">
        <em>{placeholder}</em>
      </MenuItem>

      {options.map(opt => (
        <MenuItem key={String(opt.value)} value={opt.value as any}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  )
}

export default MinimalisticDropDown
